import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

/**
 * Botão oficial "Entrar com o Google" (Google Identity Services).
 * O Client ID vem do backend (GET /auth/config); sem ele configurado o botão
 * simplesmente não aparece. O token (credential) devolvido pelo Google é
 * enviado ao backend, que valida a assinatura — o frontend nunca decide quem
 * é o usuário.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';
let configPromise = null;
let scriptPromise = null;

const loadConfig = () => {
  if (!configPromise) configPromise = api.get('/auth/config').then((r) => r.data).catch(() => ({}));
  return configPromise;
};

const loadScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = GIS_SRC;
      s.async = true;
      s.defer = true;
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error('Não foi possível carregar o login do Google.')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

export default function GoogleSignInButton({ mode = 'signin', onCredential, onUnavailable }) {
  const ref = useRef(null);
  const callbackRef = useRef(onCredential);
  const [enabled, setEnabled] = useState(false);
  callbackRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    loadConfig().then(async (cfg) => {
      if (cancelled) return;
      if (!cfg?.google_client_id) { if (onUnavailable) onUnavailable(); return; }
      try {
        await loadScript();
        if (cancelled || !ref.current) return;
        window.google.accounts.id.initialize({
          client_id: cfg.google_client_id,
          callback: (resp) => callbackRef.current?.(resp.credential),
          ux_mode: 'popup',
        });
        window.google.accounts.id.renderButton(ref.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          locale: 'pt-BR',
          width: Math.min(ref.current.offsetWidth || 336, 400),
        });
        setEnabled(true);
      } catch {
        if (onUnavailable) onUnavailable();
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return <div ref={ref} style={{ width: '100%', display: enabled ? 'flex' : 'none', justifyContent: 'center', minHeight: enabled ? 44 : 0 }} />;
}

/** Saber, sem renderizar o botão, se o login com Google está configurado. */
export const isGoogleConfigured = () => loadConfig().then((cfg) => !!cfg?.google_client_id);
